import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { authAPI } from '@/services/api';
import { CustomerCodeStep } from '@/components/common/login/CustomerCodeStep';
import { LoginForm } from '@/components/common/login/LoginForm';
import { SignupForm } from '@/components/common/login/SignupForm';
import { SettingsModal } from '@/components/common/login/SettingsModal';
import { CosmicBackground } from '@/components/common/login/CosmicBackground';
import { Logo } from '@/components/common/login/Logo';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { useGoogleOAuth } from '@/hooks/auth/useGoogleOAuth';
import { useLogin } from '@/hooks/auth/useLogin';
import { useSignup } from '@/hooks/auth/useSignup';
import { useCustomerCode } from '@/hooks/auth/useCustomerCode';
import { useFlashMessages } from '@/hooks/auth/useFlashMessages';
import { getDefaultPathForRole } from '@/config/routes';
import { rememberLoginRedirect, takeLoginRedirect } from '@/utils/loginRedirect';
import { LegalFooter } from '@/components/common/layout/LegalFooter';

const REDUCED_MOTION_KEY = 'reduced_motion';

/** 이 화면이 걸린 경로 (App.tsx). 로그인 뒤 다른 곳으로 이미 떠났는지 가릴 때 쓴다 */
const LOGIN_PAGE_PATHS = ['/', '/login'];

const LoginPage: React.FC = () => {
  const [step, setStep] = useState<1 | 2>(1); // 1: 고객사 코드 입력, 2: 로그인/회원가입
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [isSignup, setIsSignup] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  const { user, token, loading: authLoading, login } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // 서비스 로그인(/oidc/authorize)에서 왔으면 돌아갈 경로를 맡겨 둔다. 로그인 방법과 상관없이 쓴다 (#226)
  // 로그인 끝을 처리하는 아래 effect 들보다 먼저 선언한다 — 같은 커밋의 effect 는 선언 순서대로 돈다
  useEffect(() => {
    rememberLoginRedirect(window.location.search);
  }, []);

  // 커스텀 훅들
  const { customerId, saveCustomerCode, updateCustomerId, updateSaveCustomerCode, saveCustomerCodeToStorage } = useCustomerCode();
  const { error, success, setError, setSuccess, clearMessages } = useFlashMessages();
  
  // URL 파라미터에서 에러 메시지 확인
  useEffect(() => {
    const errorParam = searchParams.get('error');
    const customerCodeParam = searchParams.get('customer_code');
    const customerNameParam = searchParams.get('customer_name');
    const messageParam = searchParams.get('message');
    const approvalTypeParam = searchParams.get('approval_type');
    
    if (!errorParam) return;
    
    let errorMessage = '';
    // 에러가 아닌 안내(승인 대기 등)는 초록 안내로 보여준다 (#58)
    let noticeMessage = '';
    
    switch (errorParam) {
      case 'invalid_state':
        errorMessage = '❌ Google 로그인을 확인하지 못했습니다.\n\n로그인 화면에서 Google 로그인을 다시 시작해 주세요.';
        break;
      case 'google_email_not_verified':
        errorMessage = '❌ Google 계정의 이메일이 확인되지 않았습니다.\n\nGoogle 계정에서 이메일을 확인한 뒤 다시 시도해 주세요.';
        break;
      case 'customer_not_found':
        errorMessage = customerCodeParam 
          ? `❌ 고객사를 찾을 수 없습니다.\n\n고객사 코드 '${customerCodeParam}'를 확인해주세요.`
          : '❌ 고객사를 찾을 수 없습니다.\n\n고객 코드를 확인해주세요.';
        break;
      case 'contract_expired':
        errorMessage = customerNameParam
          ? `❌ 계약이 만료되었습니다.\n\n${customerNameParam}의 서비스 이용 계약이 만료되었습니다.\n관리자에게 문의해주세요.`
          : '❌ 계약이 만료되었습니다.\n\n서비스 이용 계약이 만료되었습니다.\n관리자에게 문의해주세요.';
        break;
      case 'google_auth_failed':
        errorMessage = '❌ Google 인증에 실패했습니다.\n\n인증 코드를 토큰으로 변환하는 과정에서 오류가 발생했습니다.\n인증 코드는 한 번만 사용할 수 있습니다. 다시 로그인해주세요.';
        break;
      case 'google_user_info_failed':
        errorMessage = '❌ 사용자 정보를 가져올 수 없습니다.\n\nGoogle에서 사용자 정보를 가져오는 과정에서 오류가 발생했습니다.\n다시 시도해주세요.';
        break;
      case 'google_email_required':
        errorMessage = '❌ 이메일 정보를 받을 수 없습니다.\n\nGoogle 계정에서 이메일 정보 제공에 동의해주세요.';
        break;
      case 'user_blocked':
      case 'domain_not_allowed':
        errorMessage = messageParam || '❌ 가입이 차단되었습니다.\n\n관리자에게 문의해주세요.';
        break;
      case 'signup_error':
        errorMessage = '❌ 회원가입 중 오류가 발생했습니다.\n\n일시적인 오류일 수 있습니다. 잠시 후 다시 시도해주세요.\n문제가 계속되면 관리자에게 문의해주세요.';
        break;
      case 'wrong_customer':
        errorMessage = customerNameParam
          ? `❌ 이 계정은 다른 기업에 속해 있습니다.\n\n${customerNameParam}에 대한 접근 권한이 없습니다.\n올바른 기업 코드로 로그인해주세요.`
          : '❌ 이 계정은 다른 기업에 속해 있습니다.\n\n올바른 기업 코드로 로그인해주세요.';
        break;
      case 'customer_deactivated':
        errorMessage = customerNameParam
          ? `❌ 기업이 비활성화되었습니다.\n\n${customerNameParam}의 서비스가 비활성화되었습니다.\n기업 관리자만 로그인할 수 있습니다.\n일반 사용자는 로그인할 수 없습니다.`
          : '❌ 기업이 비활성화되었습니다.\n\n기업 관리자만 로그인할 수 있습니다.';
        break;
      case 'pending_approval':
        // 에러가 아니라 안내다 (#58)
        noticeMessage = approvalTypeParam === 'super_admin'
          ? '회원가입이 완료되었습니다.\n슈퍼관리자의 승인을 기다리고 있습니다. 승인되면 이메일로 알려드립니다.'
          : '회원가입이 완료되었습니다.\n기업 관리자의 승인을 기다리고 있습니다. 승인되면 이메일로 알려드립니다.';
        break;
      case 'invited_account':
        errorMessage = '📧 초대된 계정입니다.\n\n관리자가 초대한 계정입니다. 회원가입을 완료해주세요.\n초대 이메일의 링크를 통해 회원가입을 진행해주세요.';
        break;
      case 'account_inactive':
        errorMessage = '❌ 계정이 활성화되지 않았습니다.\n\n관리자에게 문의해주세요.';
        break;
      default:
        errorMessage = messageParam || '❌ 오류가 발생했습니다.\n\n다시 시도해주세요.';
    }
    
    if (errorMessage || noticeMessage) {
      setError(errorMessage);
      setSuccess(noticeMessage);
      // URL에서 에러 파라미터 제거
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('error');
      newParams.delete('email');
      newParams.delete('customer_code');
      newParams.delete('customer_name');
      newParams.delete('message');
      newParams.delete('approval_type');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams, setError, setSuccess]);
  const { loading: loginLoading, error: loginError, success: loginSuccess, handleLogin: handleLoginAction } = useLogin(customerId);
  const { loading: signupLoading, error: signupError, success: signupSuccess, handleSignup: handleSignupAction } = useSignup(customerId);
  
  // 로그인/회원가입 에러/성공 메시지를 통합
  useEffect(() => {
    if (loginError) setError(loginError);
    if (loginSuccess) setSuccess(loginSuccess);
  }, [loginError, loginSuccess, setError, setSuccess]);
  
  useEffect(() => {
    if (signupError) setError(signupError);
    if (signupSuccess) setSuccess(signupSuccess);
  }, [signupError, signupSuccess, setError, setSuccess]);
  
  // Google OAuth 훅
  const {
    isGoogleProcessing,
    googleProcessingMessage,
    showPasswordFallback,
    setShowPasswordFallback,
    handleGoogleLogin,
  } = useGoogleOAuth(customerId, {
    onPending: (message) => {
      // 승인 대기는 에러가 아니다 — 안내 하나로 보여준다 (#58)
      setError('');
      setSuccess(message);
    },
    onError: (errorMsg) => {
      setError(errorMsg);
    },
  });

  // 이미 로그인한 사람이 로그인 화면에 오면 원래 가려던 곳으로, 없으면 역할별 기본 페이지로.
  // 방금 로그인을 마쳐 useLogin · useGoogleOAuth 가 이미 다른 곳으로 보냈으면 건드리지 않는다 —
  // 여기서 한 번 더 보내면 /oidc/authorize 로 돌아가던 사람이 기본 페이지로 튕긴다 (#226)
  useEffect(() => {
    if (!LOGIN_PAGE_PATHS.includes(window.location.pathname)) return;
    if (!authLoading && token && user) {
      navigate(takeLoginRedirect() || getDefaultPathForRole(user.role));
    }
  }, [authLoading, token, user, navigate]);
  
  // Google OAuth 리다이렉트 후 쿠키에서 인증 상태 확인
  useEffect(() => {
    const checkCookieAuth = async () => {
      // URL에 쿼리 파라미터가 있고, localStorage에 토큰이 없는 경우
      // (Google OAuth 리다이렉트 후 쿠키에 토큰이 있을 수 있음)
      const urlParams = new URLSearchParams(window.location.search);
      const hasQueryParams = urlParams.toString().length > 0;
      
      if (hasQueryParams && !token && !authLoading && !user) {
        try {
          // 쿠키에서 토큰 확인 (httponly 쿠키는 JavaScript에서 읽을 수 없으므로 API 호출로 확인)
          const userData = await authAPI.getCurrentUser();
          if (userData) {
            // 쿠키에 토큰이 있는 경우, 사용자 정보 설정
            // token은 'cookie'로 설정하여 쿠키 기반 인증임을 표시
            login('cookie', userData);
            
            // 리다이렉트 URL 확인 (쿼리 파라미터에서). 콘솔 안의 경로만 받는다 (#142)
            const redirectPath = takeLoginRedirect() || getDefaultPathForRole(userData.role);
            navigate(redirectPath);
          }
        } catch (error) {
          // 쿠키에 토큰이 없는 경우 무시
        }
      }
    };
    
    checkCookieAuth();
  }, [token, authLoading, user, login, navigate]);

  // 고객사 코드가 설정되면 step 2로 이동 (단, 사용자가 명시적으로 step 1로 돌아간 경우는 제외)
  const [isManuallyBacked, setIsManuallyBacked] = useState(false);
  useEffect(() => {
    if (customerId && step === 1 && !isManuallyBacked) {
      setStep(2);
    }
    // step이 2로 변경되면 수동으로 돌아간 플래그 리셋
    if (step === 2) {
      setIsManuallyBacked(false);
    }
  }, [customerId, step, isManuallyBacked]);

  // 애니메이션 감소 설정 불러오기
  useEffect(() => {
    const savedReducedMotion = localStorage.getItem(REDUCED_MOTION_KEY) === 'true';
    setReducedMotion(savedReducedMotion);
    if (savedReducedMotion) {
      document.body.classList.add('reduced-motion');
    }
  }, []);

  // 애니메이션 감소 설정 변경 핸들러
  const handleReducedMotionChange = (checked: boolean) => {
    setReducedMotion(checked);
    localStorage.setItem(REDUCED_MOTION_KEY, String(checked));
    if (checked) {
      document.body.classList.add('reduced-motion');
    } else {
      document.body.classList.remove('reduced-motion');
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    await handleLoginAction(email, password);
    // 로그인 성공 시 폼 초기화는 useLogin 훅에서 처리
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = await handleSignupAction(email, password, name, companyName);
    if (result?.success) {
      // 회원가입 성공 시 폼 초기화
      setEmail('');
      setName('');
      setCompanyName('');
      setPassword('');
      setPasswordConfirm('');
    }
  };

  const handleNext = (newCustomerId?: string) => {
    // CustomerCodeStep에서 전달된 새로운 customerId가 있으면 사용
    if (newCustomerId !== undefined) {
      updateCustomerId(newCustomerId);
    }
    saveCustomerCodeToStorage();
    setStep(2);
    clearMessages();
  };

  const handleBack = () => {
    setIsManuallyBacked(true); // 사용자가 명시적으로 step 1로 돌아감을 표시
    setStep(1);
    setEmail('');
    setName('');
    setPassword('');
    clearMessages();
    const saved = localStorage.getItem('saved_customer_code');
    updateSaveCustomerCode(!!saved && saved === customerId);
  };

  const loading = loginLoading || signupLoading;

  // 인증 로딩 중이거나 이미 로그인된 경우 아무것도 렌더링하지 않음
  if (authLoading || (token && user)) {
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden cosmic-login-bg">
      {/* 설정 아이콘 (우측 상단) */}
      <button
        onClick={() => setShowSettings(true)}
        className="fixed top-4 right-4 z-50 p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors backdrop-blur-sm"
        title="설정"
      >
        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      </button>

      {showSettings && (
        <SettingsModal
          reducedMotion={reducedMotion}
          onReducedMotionChange={handleReducedMotionChange}
          onClose={() => setShowSettings(false)}
        />
      )}

      <CosmicBackground />
      
      {/* Google OAuth 처리 중 오버레이 */}
      {isGoogleProcessing && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 shadow-2xl max-w-sm w-full mx-4">
            <LoadingSpinner size="lg" message={googleProcessingMessage} />
          </div>
        </div>
      )}
      
      <div className="max-w-md w-full space-y-8 relative z-10">
        <Logo />

        <div className="mt-8">
          {step === 1 ? (
            <CustomerCodeStep
              customerId={customerId}
              saveCustomerCode={saveCustomerCode}
              loading={loading}
              error={error}
              success={success}
              onCustomerIdChange={updateCustomerId}
              onSaveCustomerCodeChange={updateSaveCustomerCode}
              onNext={handleNext}
              onSignupClick={() => {
                setIsSignup(true);
                setStep(2);
                clearMessages();
              }}
            />
          ) : isSignup ? (
            <SignupForm
              customerId={customerId}
              email={email}
              name={name}
              companyName={companyName}
              password={password}
              passwordConfirm={passwordConfirm}
              loading={loading}
              error={error}
              success={success}
              onEmailChange={setEmail}
              onNameChange={setName}
              onCompanyNameChange={setCompanyName}
              onPasswordChange={setPassword}
              onPasswordConfirmChange={setPasswordConfirm}
              onSignup={handleSignup}
              onGoogleSignup={handleGoogleLogin}
              onBack={handleBack}
              onLoginClick={() => {
                setIsSignup(false);
                setEmail('');
                setName('');
                setCompanyName('');
                setPassword('');
                setPasswordConfirm('');
                clearMessages();
              }}
            />
          ) : (
            <LoginForm
              customerId={customerId}
              email={email}
              password={password}
              loading={loading}
              error={error}
              success={success}
              showPasswordFallback={showPasswordFallback}
              onEmailChange={setEmail}
              onPasswordChange={setPassword}
              onLogin={handleLogin}
              onGoogleLogin={handleGoogleLogin}
              onBack={handleBack}
              onSignupClick={() => {
                setIsSignup(true);
                setEmail('');
                setName('');
                setPassword('');
                setPasswordConfirm('');
                clearMessages();
              }}
              onPasswordFallbackClose={() => {
                setShowPasswordFallback(false);
                clearMessages();
              }}
            />
          )}
        </div>

      </div>

      {/* 법정 고지는 카드 밖에 넓게 둔다. 좁은 카드 안에서는 링크가 한 글자씩 쪼개졌다 (#24) */}
      <div className="w-full max-w-6xl mt-12 relative z-10">
        <LegalFooter />
      </div>
    </div>
  );
};

export default LoginPage;
